import React from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import { RadioGroup } from '@headlessui/react';
import { classNames } from '../../../Services/helpers';

const Radio = ({label, name, help, onChange, options, disabled, validators, layout, unselected, emptyMessage = "No items"}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = formData[name] ?? '';
    const {isValid, error} = useValidators(value, validators, name, updateValidity);

    const changeVal = (val) => {
        updateField(name, val);
        onChange && onChange(val);
    }

    const isDisabled = formDisabled || disabled || isSubmitting;

    const layouts = {
        'List': List,
        'SmallCards': SmallCards,
    }
    const SelectedLayout = layouts[layout] ?? layouts['List'];

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled}}>

        <fieldset>
            <legend className="sr-only">{label}</legend>
            <SelectedLayout {...{options, value, changeVal, isDisabled, emptyMessage, unselected}} />
        </fieldset>

    </CommonFieldWrapper>
}

const List = ({name, options, value, changeVal, isDisabled, emptyMessage, unselected}) => {
    return <div className="space-y-3 mt-2">

        {options.length === 0 && <p className="text-muted text-center text-sm my-2">{emptyMessage}</p>}

        {unselected && <ListItem {...{name, isDisabled, value, changeVal, label: unselected, val: ''}} />}

        {options.map((option, index) => {
            const val = typeof option === 'string' ? option : option.value;
            const label = typeof option === 'string' ? option : option.label;
            return <ListItem key={value+label} {...{val, label, option, name, isDisabled, value, changeVal}} />
    })}
    </div>
}

const ListItem = ({val, label, option, name, isDisabled, value, changeVal}) => {
    return <div key={val+label} className="relative flex items-start">
        <div className="flex h-6 items-center">
            <input
                id={'radio-'+val+label}
                aria-describedby={'desc-'+val}
                name={name}
                type="radio"
                value={val}
                className="h-4 w-4 rounded-full border-gray-300 text-brand focus:ring-brand"
                checked={value === val}
                onChange={() => changeVal(val)}
                disabled={isDisabled}
            />
        </div>
        <div className="ml-3 text-sm leading-6">
            <label htmlFor={'radio-'+val+label} className="font-medium text-gray-900 w-full">
                {label}
            </label>
            <p id={'desc-'+val+label} className="text-gray-500">
                {option?.help ?? null}
            </p>
        </div>
    </div>
}

const SmallCards = ({name, options, value, changeVal, isDisabled, emptyMessage, unselected}) => {
    return <RadioGroup value={value} onChange={changeVal} className="mt-2" disabled={isDisabled}>
        {/* <RadioGroup.Label className="sr-only">{label}</RadioGroup.Label> */}
        <div className="flex flex-row gap-2 overflow-x-auto">
            {options.length === 0 && <p className="text-muted text-center text-sm my-2">{emptyMessage}</p>}

            {unselected && <SmallCard option={{label: unselected, value: ''}} isDisabled={isDisabled} />}

            {options.map((option) => <SmallCard key={option.value} option={option} isDisabled={isDisabled} />)}
        </div>
      </RadioGroup>
}

const SmallCard = ({option, isDisabled}) => {
    return <RadioGroup.Option
        key={option.label}
        value={option.value}
        className={({ active, checked }) =>
                classNames(
                    'cursor-pointer focus:outline-none border',
                    checked
                        ? (isDisabled ? 'bg-blue-300 text-gray-100' : 'bg-slate-50 border-brand text-brand')
                        : (isDisabled ? 'bg-slate-50 text-gray-600' : 'bg-white text-gray-900 hover:bg-gray-50'),
                    'flex items-center justify-center rounded-lg py-2 px-3 text-sm sm:flex-1',
                    checked ? 'font-semibold' : 'font-normal',
                )
        }
        disabled={isDisabled}
    >
        <RadioGroup.Label as="span">{option.label}</RadioGroup.Label>

    </RadioGroup.Option>
}

export default Radio;
